import { Mutation, Action } from 'vuex-module-decorators';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { EdgePatientEnrollmentApi } from '@trialcard/apigateway.client/edgePatientEnrollmentApi';
import {
    EnrollPatientRequestModel,
    EnterpriseCoordinatorAccountUpsearchRequestModel,
    UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
    EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    EnterpriseCoordinatorSharedAddressUpsearchModel,
    EnterpriseCoordinatorSharedEmailAddressUpsearchModel,
    EnterpriseCoordinatorSharedPhoneUpsearchModel,
    UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
    EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel,
    UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
} from '@trialcard/apigateway.models';
import { ContactInfoType, Gender, RoleType } from '@trialcard/enums';

import moment, { Moment } from 'moment';
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { EdgeEnrollmentApi, EscoordV1Api } from '@trialcard/apigateway.client';
import DocusignModule from './docusign';
import { Settings } from '~/services/settings';

import SurveyModule from '~/store/survey';

import { rolling365 } from '~/tests/e2e/@data/shared/dates'
import { pharmacyMN } from '~/tests/e2e/@data/shared/memberNumbers'
import { CommonTools } from '~/components/tools/commonTools';
import { Field } from '~/types/docuSign/Fields';
import EligibilityModule from '~/store/eligibility';
import { trackEvent, trackException } from '~/services/telemetry';


@InjectModule({ stateFactory: true }, module)
export default class EnrollmentModule extends InjectVuexModule {
    @Resolve
    public patientEnrollmentApi!: EdgePatientEnrollmentApi;

    @Resolve
    public enrollmentApi!: EdgeEnrollmentApi;

    @Resolve
    public settings!: Settings;

    @Resolve
    public survey!: SurveyModule;

    @Resolve
    public escoord!: EscoordV1Api;

    @Resolve
    docusign!: DocusignModule;

    @Resolve
    public eligibility!: EligibilityModule;


    isCareGiver: boolean | null = null;
    public testPDF = false;

    // CENTRAL SUBMISSION GUARD FLAG
    isEnrollmentInProgress = false;

    defaultPatientInformationData = {
        firstName: '' as string,
        lastName: '' as string,
        dateOfBirth: null as Moment | null,
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        emailAddresses: [] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
        gender: null as Gender | null,
    };

    defaultCareGiverInformationData = {
        firstName: '' as string,
        lastName: '' as string,
        dateOfBirth: null as Moment | null,
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        emailAddresses: [] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
        gender: null as Gender | null,
    };

    defaultInsuranceInformationData = {
        healthPlanName: '' as string,
        memberNumber: '' as string,
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        faxNumber: '' as string,
        coPay: '' as string,
    };

    defaultPrescriberData = [
        {
            model: {
                firstName: '',
                lastName: '',
                npi: '',
                accountRelationshipIsPrimary: true,
                prescriberLicenses: [],
                externalIds: [],
                addresses: [],
                phoneNumbers: [],
                emailAddresses: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[];

    defaultSiteData = [
        {
            model: {
                accountRelationshipIsPrimary: true,
                siteRoleType: RoleType.PrescriberSite,
                pointOfContacts: [],
                description: '',
                addresses: [
                    {
                        addressOne: '',
                        addressTwo: '',
                        zip: '',
                        city: '',
                        state: '',
                    },
                ],
                contacts: [],
                emailAddresses: [],
                externalIds: [],
                phoneNumbers: [],
                globalIds: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[];

    defaultPayorData = [
        {
            model: {
                payorId: (null as unknown) as number,
                payorType: (null as unknown) as number,
                accountRelationshipIsPrimary: true,
                payorGroup: '',
                payorName: '',
                webAddresses: [],
                addresses: [],
                phoneNumbers: [
                    {
                        phoneType: 1,
                        phoneNumber: '',
                        phonePurpose: 1,
                    },
                    {
                        phoneType: 3,
                        phoneNumber: '',
                        phonePurpose: 8,
                    },
                ],
                emailAddresses: [],
                contacts: [],
                payorBenefit: {
                    dateOfBirth: null as Moment | null,
                    policyHolderFirstName: '',
                    policyHolderLastName: '',
                    relationshipToPatient: undefined,
                    policyNumber: '',
                    planType: undefined,
                },
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel[];

    defaultContactPreferences = [] as EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[];

    defaultMemberNumber = pharmacyMN.number
    surveySession = ""
    defaultExpirationDate = rolling365

    defaultDemographicData = {
        firstName: '' as string,
        lastName: '' as string,
        dateOfBirth: '' as any,
        gender: null as any,
        zip: '' as string,
    };

    defaultAuthorizationData = {
        doesAgreeTextMessageOne: false as boolean,
        doesAgreeTextMessageTwo: false as boolean,
        doesAgreeTextMessageThree: false as boolean,
        doesAgreeTextMessageFour: false as boolean,
        legalGuardianConfirmationMessage: false as boolean,
    }

    memberNumber = this.defaultMemberNumber;
    expirationDate = this.defaultExpirationDate;
    patientInformationData = { ...this.defaultPatientInformationData };
    careGiverInformationData = { ...this.defaultCareGiverInformationData };
    insuranceInformationData = { ...this.defaultInsuranceInformationData };
    prescriberData = [ ...this.defaultPrescriberData ];
    siteData = [ ...this.defaultSiteData ];
    payorData = [ ...this.defaultPayorData ];
    contactPreferences = [...this.defaultContactPreferences];
    authorizationData = { ...this.defaultAuthorizationData }
    demographicData = { ...this.defaultDemographicData };
    activeProgramId: number | null = null;
    isUnderinsured = false;
    shouldMockEservices = false;
    mockEservicesOptions: {
        mockShouldSucceedPcfHasInsurance?: boolean;
        mockShouldSucceedFasWithinFpl?: boolean;
        mockShouldBeGovernmentProgramEligible?: boolean;
    } = {};
    accountId = (null as unknown) as number;

    calculateAgeFromDob(dateOfBirth: any) {
        if (!dateOfBirth) return null;

        return moment().diff(moment(dateOfBirth), 'years');
    }

    structureCreateEnvelopeRequest(accountId:number,programId: number) {
        const {
            dateOfBirth,
            emailAddresses,
            phoneNumbers,
        } = this.patientInformationData

        const firstName = CommonTools.titleCase(this.patientInformationData.firstName || '');
        const lastName = CommonTools.titleCase(this.patientInformationData.lastName || '');
        const patEmail = emailAddresses.length > 0 && emailAddresses[0].address || '';

        const age = dateOfBirth
            ? moment().diff(moment(dateOfBirth), 'years')
            : null;

        const isAge17 = age === 17;


        const phone = phoneNumbers.length > 0 && phoneNumbers[0].phoneNumber || '';
        const patName = CommonTools.titleCase(`${firstName} ${lastName}`);
        const zipCode = this.patientInformationData.addresses.length > 0 && this.patientInformationData.addresses[0].zip || '';

        const repFirstName = CommonTools.titleCase(this.careGiverInformationData.firstName || '');
        const repLastName = CommonTools.titleCase(this.careGiverInformationData.lastName || '');
        const repName = CommonTools.titleCase(`${repFirstName} ${repLastName}`);

        const fields: Field[] = isAge17
            ? [
                {
                    name: 'Rep_First_Name',
                    value: repFirstName,
                },
                {
                    name: 'Rep_Last_Name',
                    value: repLastName,
                },
                {
                    name: 'Pat_First_Name',
                    value: firstName,
                },
                {
                    name: 'Pat_Last_Name',
                    value: lastName,
                },
                {
                    name: 'Pat_DOB',
                    value: dateOfBirth
                        ? moment(dateOfBirth).format('L')
                        : '',
                },
            ]
            : [
                {
                    name: 'Pat_Full_Name',
                    value: patName,
                },
                {
                    name: 'Pat_First',
                    value: firstName,
                },
                {
                    name: 'Pat_Last',
                    value: lastName,
                },
                {
                    name: 'Pat_DOB',
                    value: dateOfBirth
                        ? moment(dateOfBirth).format('L')
                        : '',
                },
                {
                    name: 'Pat_Email',
                    value: patEmail,
                },
                {
                    name: 'Pat_Primary_Ph',
                    value: phone,
                },
                {
                    name: 'Pat_ZIP',
                    value: zipCode,
                },
            ];

        const request = {
            accountId,
            programId,
            documentType: 'SRF', // Hard coded for now based on documentation from Team Code Brigade
            brandId: isAge17
                ? this.settings.age17BrandId
                : this.settings.brandId,
            senderId: isAge17
                ? this.settings.age17SenderId
                : this.settings.senderId,
            templateId: isAge17
                ? this.settings.age17TemplateId
                : this.settings.templateId,
            replyEmailAddressOverride: 'no-reply@docusign.net',
            expireInDays: +this.settings.expireInDays,
            emailSubject: ' ',
            emailBody: ' ',
            send: true, // Send needs to be set to true to request a RecipientView.
            embedded: true, // Embedded needs to be set to true for envelope intended for RecipientView
            recipients: [
                {
                    name: isAge17 ? repName : patName,
                    email:  patEmail,
                    role: isAge17 ? "LGREP" : "Patient",
                }
            ],
            fields
        };
        return request;
    }
    @Mutation
    public async setShouldMockEservices(shouldMockEservices: boolean) {
        this.shouldMockEservices = shouldMockEservices;
    }

    @Mutation
    public async setMockEservicesOptions(options: typeof EnrollmentModule.prototype.mockEservicesOptions) {
        this.mockEservicesOptions = { ...options };
    }

    @Mutation
    public async updatePatientInformation(newPatientInformation: typeof EnrollmentModule.prototype.patientInformationData) {
        this.patientInformationData = { ...this.patientInformationData, ...newPatientInformation };
    }

    @Mutation
    public async updatePrescribers(newPrescriberData: UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[]) {
        this.prescriberData = [...newPrescriberData];
    }

    @Mutation
    public async updateSites(newSites: UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[]) {
        this.siteData = [...newSites];
    }

    @Mutation
    public async updatePayors(newPayorData: UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel[]) {
        this.payorData = [...newPayorData];
    }

    @Mutation
    public async updateCareGiverInformation(newCareGiverInformation: typeof EnrollmentModule.prototype.careGiverInformationData) {
        this.careGiverInformationData = { ...this.careGiverInformationData, ...newCareGiverInformation };
    }

    @Mutation
    public async updateInsuranceInformation(newInsuranceInformation: typeof EnrollmentModule.prototype.insuranceInformationData) {
        this.insuranceInformationData = { ...this.insuranceInformationData, ...newInsuranceInformation };
    }

    @Mutation
    public async updateContactPreferences(newContactPreferences: EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[]) {
        this.contactPreferences = [...newContactPreferences];
    }

    @Mutation
    public setMemberNumber(memberNumber: string) {
        this.memberNumber = memberNumber;
    }
    @Mutation
    public setSurveySession(surveySession: string) {
        this.surveySession = surveySession;
    }
    @Mutation
    public setExpirationDate(expirationDate: Moment) {
        this.expirationDate = expirationDate;
    }

    @Mutation
    public setIsCaregiver(isCareGiver: boolean | null) {
        this.isCareGiver = isCareGiver;
    }

    @Mutation
    updateAuthorizationData(newAuthorizationData: typeof EnrollmentModule.prototype.defaultAuthorizationData) {
        this.authorizationData = { ...newAuthorizationData };
    }

    @Mutation
    public async updateAccountId(accountId: any) {
        this.accountId =  accountId;
    }

    @Mutation
    setEnrollmentInProgress(value: boolean) {
        this.isEnrollmentInProgress = value;
    }

    @Mutation
    public setTestPDF(testPDF: boolean): void {
        this.testPDF = testPDF;
    }

    get enrollmentInProgress() {
        return this.isEnrollmentInProgress;
    }
    structureRecipientViewUrlRequest(accountId:number, programId: number) {
        const {
            dateOfBirth,
            emailAddresses
        } = this.patientInformationData

        const age = dateOfBirth
            ? moment().diff(moment(dateOfBirth), 'years')
            : null;
        const isAge17 = age === 17;
        const repFirstName = CommonTools.titleCase(this.careGiverInformationData.firstName || '');
        const repLastName = CommonTools.titleCase(this.careGiverInformationData.lastName || '');
        const repName = CommonTools.titleCase(`${repFirstName} ${repLastName}`);
        const firstName = CommonTools.titleCase(this.patientInformationData.firstName || '');
        const lastName = CommonTools.titleCase(this.patientInformationData.lastName || '');
        const patName = CommonTools.titleCase(`${firstName} ${lastName}`);
        const patEmail = emailAddresses.length > 0 && emailAddresses[0].address || '';
        const request = {
            programId,
            accountId,
            envelopeId: this.docusign.envelopeId,
            email:patEmail,
            name: isAge17 ? repName : patName,
            returnUrl: `${CommonTools.getBaseUrl(window.location.href, this.settings)}enroll-complete`,
        };
        return request;
    }
    @Action({ rawError: true })
    public clearStore() {
        this.setMemberNumber(this.defaultMemberNumber);
        this.setExpirationDate(this.defaultExpirationDate);
        this.updatePatientInformation(this.defaultPatientInformationData);
        this.updateCareGiverInformation(this.defaultCareGiverInformationData);
        this.updateInsuranceInformation(this.defaultInsuranceInformationData);
        this.updatePrescribers(this.defaultPrescriberData);
        this.updateSites(this.defaultSiteData);
        this.updatePayors(this.defaultPayorData);
        this.updateContactPreferences(this.defaultContactPreferences);
        this.updateAuthorizationData(this.defaultAuthorizationData);
        this.updateDemographicData(this.defaultDemographicData);
        this.setActiveProgramId(null);
        this.setIsUnderinsured(false);
        this.updateDemographicData(this.defaultDemographicData)
    }

    @Action({ rawError: true })
    public clearEnrollment() {
        this.updatePatientInformation({ ...this.defaultPatientInformationData });
        this.setMemberNumber(this.defaultMemberNumber);
        this.updateAuthorizationData(this.defaultAuthorizationData);
    }

    @Mutation
    public updateDemographicData(newData: typeof EnrollmentModule.prototype.demographicData) {
        this.demographicData = { ...this.demographicData, ...newData };
    }

    @Mutation
    public setActiveProgramId(programId: number | null) {
        this.activeProgramId = programId;
    }

    @Mutation
    public setIsUnderinsured(value: boolean) {
        this.isUnderinsured = value;
    }

    @Action({ rawError: true })
    public async submitToAPIGetBestEnrollmentsV2(programId?: number) {
        let response = null;
        try {
            const dob = this.demographicData.dateOfBirth;
            const dobIso = dob
                ? moment.isMoment(dob)
                    ? (dob as Moment).toISOString()
                    : moment.utc(dob as any).toISOString()
                : null;

            const payload = {
                firstName: this.demographicData.firstName,
                lastName: this.demographicData.lastName,
                dateOfBirth: dobIso,
                zip: this.demographicData.zip,
                gender: this.demographicData.gender,
                includeQueuedTransient: true,
                includeRejected: true,
                removeTestPatients: false,
            };

            const axiosInstance: AxiosInstance = (this.enrollmentApi as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: '/edge/enrollment/GetBestEnrollmentsV2',
                method: 'POST',
                data: payload,
                headers: {
                    'x-program-id': programId ? String(programId) : undefined,
                },
            };

            await Promise.all([
                (this.enrollmentApi as any).authentications.ApiKey.applyToRequest(axiosRequest),
                (this.enrollmentApi as any).authentications.Bearer.applyToRequest(axiosRequest),
                (this.enrollmentApi as any).authentications.Token.applyToRequest(axiosRequest),
            ]);

            response = await axiosInstance.request(axiosRequest);
        } catch {
            response = { status: 400 } as AxiosResponse;
        }
        return response;
    }

    @Action({ rawError: true })
    public async submitToAPI(enrollCall = false) {
            // Check if already submitting
        if (this.isEnrollmentInProgress) {
            // Diagnostic: the store mutex blocked a concurrent submission. For the
            // real enroll call this is exactly the guard we rely on; logging it
            // tells us whether the mutex is engaging when it should.
            if (enrollCall) {
                trackEvent('Enroll.Suppressed', {
                    source: 'store-mutex',
                    enrollmentInProgress: this.isEnrollmentInProgress,
                });
            }
            return null; // Prevent duplicate API submissions
        }

         this.setEnrollmentInProgress(true);// Lock submission
        // Diagnostic correlation id for one enroll attempt. Doubles as the seed
        // for a future server-side Idempotency-Key if that fix is ever built.
        const enrollAttemptId = enrollCall
            ? Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10)
            : '';
        let enrollStartMs = 0;
        let response = null;
        let responseAccount = null;
        const age = this.patientInformationData.dateOfBirth
            ? moment().diff(moment(this.patientInformationData.dateOfBirth), 'years')
            : null;

        const isAge17 = age === 17;
        const noPrescriptionAnswerId = Number(this.settings.eligibilityPurchasePrescriptionAnswerIdNoPrescription);
        const purchasePrescriptionQuestionId = Number(this.settings.eligibilityPurchasePrescriptionQuestionId);

        const purchasePrescriptionEntry = this.eligibility.eligibilityData.find(
            item => item.questionId === purchasePrescriptionQuestionId
        );

        const isUninsured =
            purchasePrescriptionEntry &&
            purchasePrescriptionEntry.answerId === noPrescriptionAnswerId;
        const shouldSendPayorData = !isUninsured;
        const {
            shouldMockEservices,
            mockEservicesOptions,
        } = this;

        const contactPreferences: EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[] = [
            { contactInfoType: ContactInfoType.EMAIL, isOptedIn: true, isPreferred: false },
        ];

        if (this.authorizationData.doesAgreeTextMessageFour) {
            contactPreferences.push({ contactInfoType: ContactInfoType.Text, isOptedIn: true, isPreferred: false });
        }
        try {
            const patientPhoneNumbers = this.patientInformationData.phoneNumbers || [];

            const adjustedPatientInformationData = {
                ...this.patientInformationData,
                phoneNumbers: isAge17 ? [] : patientPhoneNumbers,
            };

            const adjustedCareGiverInformationData = {
                ...this.careGiverInformationData,
                phoneNumbers: isAge17 ? patientPhoneNumbers : this.careGiverInformationData.phoneNumbers,
            };
        const patient_payor = {
                patientOutOfPocket: this.insuranceInformationData.coPay.replace("$",""),
                account: {
                    patientData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            ...adjustedPatientInformationData,
                            middleName: '',
                            suffix: '',
                            nickname: '',
                        } as EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    } as UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    accountData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            accountType: 1,
                            accountStatus: 1,
                            contactPreferences,
                            expireExistingAccountRelationships: true,
                            contacts: isAge17
                            ? [
                                {
                                    roleType: RoleType.LegalRepresentative,
                                    firstName: this.careGiverInformationData.firstName || '',
                                    lastName: this.careGiverInformationData.lastName || '',
                                    altPhoneNumber:
                                        adjustedCareGiverInformationData.phoneNumbers.length > 0
                                            ? adjustedCareGiverInformationData.phoneNumbers[0].phoneNumber
                                            : '',
                                    phoneNumber: '',
                                },
                            ]
                            : [],
                        },
                    } as UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
                    ...(shouldSendPayorData ? { payorData: this.payorData } : {}),
                    surveySessionData: [this.survey.sessionId] as number[],
                } as EnterpriseCoordinatorAccountUpsearchRequestModel,
                accountAttestations: [{code: "PatSignFlag", startDate: moment()}]
            } as any;

     const patient = {
                patientOutOfPocket: this.insuranceInformationData.coPay.replace("$",""),
                account: {
                    patientData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                           ...adjustedPatientInformationData,
                            middleName: '',
                            suffix: '',
                            nickname: '',
                        } as EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    } as UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    accountData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            accountType: 1,
                            accountStatus: 1,
                            contactPreferences,
                            contacts: isAge17
                            ? [
                                {
                                    roleType: RoleType.LegalRepresentative,
                                    firstName: this.careGiverInformationData.firstName || '',
                                    lastName: this.careGiverInformationData.lastName || '',
                                    altPhoneNumber:adjustedCareGiverInformationData.phoneNumbers.length > 0
                                        ? adjustedCareGiverInformationData.phoneNumbers[0].phoneNumber
                                        : '',
                                    phoneNumber: '',
                                },
                            ]
                            : [],
                        },
                    } as UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
                    ...(shouldSendPayorData ? { payorData: this.payorData } : {}),
                    surveySessionData: [this.survey.sessionId] as number[],
                } as EnterpriseCoordinatorAccountUpsearchRequestModel,
                accountAttestations: [{code: "PatSignFlag", startDate: moment()}]
            } as any;

            // Please see the documentation for this functionality in the setMockEservices method in default.vue
            if (shouldMockEservices) {
                patient.eServicesOptions = { ...mockEservicesOptions };
            }

            const values = (this.eligibility && this.eligibility.eligibilityData) || [];

            const purchasePrescriptionAnswer =
                values.length > 1 &&
                values[1] &&
                values[1].answerId
                    ? values[1].answerId.toString()
                    : '';

            const isGovernmentSponsored =
                (this.eligibility &&
                    this.eligibility.prescriptionInsuranceType === 'government-sponsored') ||
                purchasePrescriptionAnswer ===
                    this.settings.eligibilityPurchasePrescriptionAnswerIdGovernment;

            const programId = isGovernmentSponsored
                ? this.settings.governmentSponsoredProgramId!
                : this.settings.uninsuredProgramId!;
            if(!enrollCall){
                responseAccount = await this.escoord.accountUpsearchByTenantIdProgramId( patient_payor.account, +programId!);
                const tempAccountID = (responseAccount.data && responseAccount.data.data && responseAccount.data.data.accountId ) || ''
                const createEnvelopeSuccess = await this.docusign.createEnvelope(this.structureCreateEnvelopeRequest(+tempAccountID,+programId));
                const requestUrlSuccess = createEnvelopeSuccess && await this.docusign.requestURL(this.structureRecipientViewUrlRequest(+tempAccountID,+programId)) || null;
                this.updateAccountId(tempAccountID);
                return true
            }
            else{
                const axios: AxiosInstance = (this.patientEnrollmentApi as any).axios;
                const axiosRequest: AxiosRequestConfig = {
                    url: '/edge/enrollment/EServicesEnrollment/enroll', // new endpoint for enrolling with eServices
                    method: 'POST',
                    data: patient,
                    headers: {
                        'x-program-id': this.settings.programId!,
                    },
                };

                await Promise.all([(this.patientEnrollmentApi as any).authentications.Token.applyToRequest(axiosRequest)]);

                // Diagnostic: this attempt passed every guard and is about to hit
                // the wire. `enrollmentInProgressAsRead` is the mutex value as seen
                // INSIDE the action — if a second Dispatched ever logs this as
                // false while another attempt is still in flight, that proves the
                // mutex read is not reliable inside the vuex action.
                enrollStartMs = Date.now();
                trackEvent('Enroll.Dispatched', {
                    attemptId: enrollAttemptId,
                    enrollmentInProgressAsRead: this.isEnrollmentInProgress,
                    sentAtMs: enrollStartMs,
                    surveySessionId: this.survey.sessionId,
                });

                response = await axios.request(axiosRequest);

                const enrollResponse = response as any;
                trackEvent('Enroll.Completed', {
                    attemptId: enrollAttemptId,
                    httpStatus: enrollResponse && enrollResponse.status ? enrollResponse.status : '',
                    durationMs: Date.now() - enrollStartMs,
                    surveySessionId: this.survey.sessionId,
                });
            }
            } catch (error) {
            // Handle error
                response = { status: 400 } as AxiosResponse;
                if (enrollCall) {
                    const err = error as any;
                    const httpStatus = err && err.response && err.response.status ? err.response.status : '';
                    trackEvent('Enroll.Failed', {
                        attemptId: enrollAttemptId,
                        httpStatus,
                        durationMs: enrollStartMs ? Date.now() - enrollStartMs : 0,
                        surveySessionId: this.survey.sessionId,
                    });
                    trackException(error, { event: 'Enroll.Failed', attemptId: enrollAttemptId });
                }
            } finally {
                 this.setEnrollmentInProgress(false);// Always unlock after attempt
            }
        return response;
    }
}
